// Global Types
import type {
  $Children,
  $Component,
} from 'common.types';
import type {
  $Translate,
} from 'common.language';

// Global Components
import * as React from 'react';

// Helpers
import {
  animationClass,
  scrollTop,
} from '@vroskus/library-helpers/dist/browser';

// Types
type $OptionalProps = {
  className?: string;
  noMargin?: boolean;
  rightComponent?: $Component<unknown>;
  shorten?: boolean;
  shortenButtonLabel?: string;
  title?: string;
  transparent?: boolean;
};

type $Props = $OptionalProps & $Translate & {
  children: $Children;
  id: string;
};

type $State = {
  showAll: boolean;
};

class SectionView extends React.Component<$Props, $State> {
  static defaultProps: $OptionalProps = {
    className: undefined,
    noMargin: undefined,
    rightComponent: undefined,
    shorten: undefined,
    shortenButtonLabel: undefined,
    title: undefined,
    transparent: undefined,
  };

  constructor(props: $Props) {
    super(props);

    this.state = {
      showAll: false,
    };
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  renderTitle() {
    const {
      rightComponent,
      title,
      transparent,
    } = this.props;

    if (title) {
      return (
        <div className={`mb-3 ${transparent ? 'bg-white rounded-lg -mx-5 -mt-5 p-5' : ''}`}>
          <div className={'flex flex-row'}>
            <h2 className={'h3 w-full'}>{title}</h2>
            {rightComponent && (
              <div className={'flex flex-row justify-end'}>
                {rightComponent}
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  }

  renderShowAllToggleButton() {
    const {
      id,
      shortenButtonLabel,
      t,
    } = this.props;
    const {
      showAll,
    } = this.state;

    return (
      <div className={`text-center ${showAll === true ? '-mb-2 mt-8' : '-m-5 p-5 bg-white rounded-b-lg'}`}>
        <button
          className={'btn btn_secondary btn_outlined uppercase py-1'}
          onClick={async () => {
            await this.setStateAsync({
              showAll: !showAll,
            });

            if (showAll === true) {
              scrollTop(id);
            }
          }}
          type={'button'}
        >
          {showAll === true ? t('Show less') : shortenButtonLabel || t('Show all')}
        </button>
      </div>
    );
  }

  renderContent() {
    const {
      children,
      shorten,
    } = this.props;
    const {
      showAll,
    } = this.state;

    return shorten === true ? (
      <>
        <div className={`-mx-5 ${showAll === true ? '-mb-5' : 'fade-content max-h-80'}`}>
          {children}
        </div>
        {this.renderShowAllToggleButton()}
      </>
    ) : <div>{children}</div>;
  }

  render() {
    const {
      className,
      id,
      noMargin,
      transparent,
    } = this.props;

    let containerClass = `Section card ${noMargin ? '' : 'p-5'} mb-5 ${className || ''} ${animationClass('fadeIn')}`;

    if (transparent) {
      containerClass = `${containerClass} bg-white-tr`;
    }

    return (
      <div
        className={containerClass}
        id={id}
      >
        {this.renderTitle()}
        {this.renderContent()}
      </div>
    );
  }
}

export default SectionView;
